import { useEffect, useState } from "react";
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/dashboard/Shared";

const ICON = { success: CheckCircle2, warning: AlertTriangle, error: XCircle, info: Info };
const TINT = {
  success: "text-[#00E676] bg-[#00E676]/15 border-[#00E676]/30",
  warning: "text-[#FFD600] bg-[#FFD600]/15 border-[#FFD600]/30",
  error: "text-[#FF3B30] bg-[#FF3B30]/15 border-[#FF3B30]/30",
  info: "text-[#00F0FF] bg-[#7000FF]/15 border-[#7000FF]/30",
};

const PREF_ROWS = [
  { key: "system_notifications", label: "System Notifications", hint: "KYC, subscription, MT5 and account activity emails" },
  { key: "reports_weekly", label: "Weekly Performance Report", hint: "Every Monday morning" },
  { key: "reports_monthly", label: "Monthly Performance Report", hint: "First of every month" },
  { key: "reports_daily", label: "Daily Summary", hint: "End-of-day performance snapshot" },
  { key: "marketing", label: "Product Updates & Announcements", hint: "New features, insights, occasional offers" },
];

export default function Notifications() {
  const [items, setItems] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [prefsBusy, setPrefsBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/notifications", { params: { limit: 100 } }),
    api.get("/notifications/preferences"),
  ]).then(([a, b]) => { setItems(a.data.items || []); setPrefs(b.data); }).catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.post("/notifications/mark-all-read"); load(); };
  const remove = async (id) => { await api.delete(`/notifications/${id}`); load(); };
  const togglePref = async (key, next) => {
    setPrefsBusy(true);
    try {
      const { data } = await api.patch("/notifications/preferences", { [key]: next });
      setPrefs(data);
      toast.success("Preferences saved");
    } catch { toast.error("Could not save"); }
    finally { setPrefsBusy(false); }
  };

  if (!items) return <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-16" />)}</div>;

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title="Notifications" sub="System alerts, subscription events and report summaries">
        {items.some((n) => !n.read) && (
          <button data-testid="mark-all-read-button" onClick={markAll} className="btn-outline px-4 py-2 text-xs">Mark all as read</button>
        )}
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState title="No notifications" sub="You're all caught up." />
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => {
            const Icon = ICON[n.type] || Info;
            return (
              <div key={n.notif_id} className={`card-dark p-4 flex gap-4 ${n.read ? "opacity-60" : ""}`}>
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${TINT[n.type] || TINT.info}`}>
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
                  <p className="text-[11px] text-gray-600 mt-1 tabular">{String(n.created_at).slice(0, 16).replace("T", " ")}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#00F0FF] shrink-0 mt-1" />}
                  <button onClick={() => remove(n.notif_id)} className="text-gray-500 hover:text-[#FF3B30] text-xs" title="Delete">×</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Email Preferences */}
      <div className="card-dark p-6" data-testid="email-preferences">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-[#7000FF]" />
          <h3 className="text-white text-base font-medium">Email Preferences</h3>
        </div>
        <p className="text-xs text-gray-500 mb-5">Security emails (OTP, password reset) are always sent regardless of preferences.</p>
        {!prefs ? <div className="skeleton h-32" /> : (
          <div className="divide-y divide-white/5">
            {PREF_ROWS.map((r) => (
              <label key={r.key} className="flex items-center justify-between py-3 cursor-pointer">
                <div>
                  <p className="text-sm text-white">{r.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{r.hint}</p>
                </div>
                <input
                  type="checkbox"
                  data-testid={`pref-${r.key}`}
                  checked={!!prefs[r.key]}
                  disabled={prefsBusy}
                  onChange={(e) => togglePref(r.key, e.target.checked)}
                  className="w-9 h-5 accent-[#7000FF]"
                />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
